/**
 * Gemeinsame TypeScript-Interfaces für die erweiterte Element-Bibliothek
 */

// Tab-Typen für ElementLibraryPro
export type ElementTabType =
  | 'icons'
  | 'shapes'
  | 'frames'
  | 'layouts'
  | 'videos'
  | 'illustrations'
  | 'animations'
  | 'blobs'
  | 'mockups'
  | 'charts'
  | 'tables'
  | 'audio';

// Tab-Konfiguration
export interface ElementTab {
  id: ElementTabType;
  label: string;
  icon: string;
  count: string;
  description?: string;
}

export const ELEMENT_TABS: ElementTab[] = [
  { id: 'icons', label: 'Icons', icon: '🎨', count: '9000+', description: 'Phosphor Icons' },
  { id: 'shapes', label: 'Formen', icon: '⬡', count: '60+', description: 'Grundformen' },
  { id: 'frames', label: 'Rahmen', icon: '🖼️', count: '20+', description: 'Foto-Rahmen' },
  { id: 'layouts', label: 'Raster', icon: '⊞', count: '15+', description: 'Grid-Layouts' },
  { id: 'videos', label: 'Videos', icon: '🎬', count: 'Pexels', description: 'Stock Videos' },
  { id: 'illustrations', label: 'Grafiken', icon: '🎭', count: '50+', description: 'unDraw' },
  { id: 'animations', label: 'Animation', icon: '✨', count: 'Lottie', description: 'Animiert' },
  { id: 'blobs', label: 'Blobs', icon: '🫧', count: '∞', description: 'Generator' },
  { id: 'mockups', label: 'Mockups', icon: '📱', count: '10+', description: 'Geräte' },
  { id: 'charts', label: 'Diagramme', icon: '📊', count: '6', description: 'Charts' },
  { id: 'tables', label: 'Tabellen', icon: '📋', count: '3', description: 'Editierbar' },
  { id: 'audio', label: 'Audio', icon: '🎵', count: '50+', description: 'Musik & SFX' },
];

// Basis-Interface für alle Library-Elemente
export interface BaseLibraryElement {
  id: string;
  name: string;
  tags: string[];
  category?: string;
}

// Frame-Element (Foto-Rahmen)
export interface FrameElement extends BaseLibraryElement {
  type: 'frame';
  svg: string;
  frameStyle: 'polaroid' | 'circle' | 'vintage' | 'modern' | 'decorative' | 'minimal';
  aspectRatio?: number;
  imageArea: { x: number; y: number; width: number; height: number }; // Prozent-Werte
}

// Layout-Element (Grid-Vorlagen)
export interface LayoutElement extends BaseLibraryElement {
  type: 'layout';
  thumbnail: string; // SVG-Vorschau
  gridType: 'columns' | 'rows' | 'mixed' | 'masonry';
  placeholders: Array<{
    id: string;
    type: 'image' | 'text' | 'shape';
    position: { x: number; y: number };
    size: { width: number; height: number };
    label?: string;
  }>;
}

// Video-Element (Pexels Stock)
export interface VideoElement extends BaseLibraryElement {
  type: 'video';
  source: 'pexels';
  thumbnailUrl: string;
  videoUrl: string;
  duration: number; // Sekunden
  width: number;
  height: number;
  quality: 'hd' | 'sd' | '4k';
  attribution: {
    photographer: string;
    photographer_url: string;
    source_url: string;
  };
}

// Illustration-Element (unDraw)
export interface IllustrationElement extends BaseLibraryElement {
  type: 'illustration';
  svg: string;
  primaryColor: string; // Standard-Farbe die ersetzt werden kann
  illustrationStyle: 'outline' | 'flat' | 'duotone';
}

// Animation-Element (Lottie)
export interface AnimationElement extends BaseLibraryElement {
  type: 'animation';
  animationData: object; // Lottie JSON
  duration: number; // Millisekunden
  loop: boolean;
  autoplay: boolean;
}

// Blob-Element (generiert)
export interface BlobElement extends BaseLibraryElement {
  type: 'blob';
  svg: string;
  complexity: number; // 3-10
  contrast: number; // 0-1
  seed: number;
}

// Mockup-Element (Geräte-Rahmen)
export interface MockupElement extends BaseLibraryElement {
  type: 'mockup';
  svg: string;
  deviceType: 'phone' | 'tablet' | 'laptop' | 'desktop' | 'watch' | 'apparel';
  screenArea: { x: number; y: number; width: number; height: number; borderRadius?: number };
  perspective?: { rotateX: number; rotateY: number };
}

// Chart-Element (Diagramme)
export interface ChartElement extends BaseLibraryElement {
  type: 'chart';
  chartType: 'bar' | 'line' | 'pie' | 'donut' | 'area';
  defaultData: Array<{ name: string; value: number; color?: string }>;
  colors: string[];
}

// Table-Element (Tabellen)
export interface TableElement extends BaseLibraryElement {
  type: 'table';
  rows: number;
  columns: number;
  style: 'minimal' | 'striped' | 'bordered' | 'modern';
  defaultData?: {
    headers: string[];
    rows: string[][];
  };
}

// Audio-Element
export interface AudioElement extends BaseLibraryElement {
  type: 'audio';
  audioUrl: string;
  duration: number; // Sekunden
  audioType: 'music' | 'sfx' | 'ambient' | 'voiceover';
  attribution?: {
    artist: string;
    source: string;
  };
}

// Union-Type für alle Element-Typen
export type ExtendedLibraryElement =
  | FrameElement
  | LayoutElement
  | VideoElement
  | IllustrationElement
  | AnimationElement
  | BlobElement
  | MockupElement
  | ChartElement
  | TableElement
  | AudioElement;
